import React from 'react';
import millify from 'millify';
import { gql } from '@soundxyz/gql-string';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { FragmentType } from '@graphql/reactQuery';
import { CollectorMintsRowFragmentDoc, getFragment } from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorMintsRow on ArtistCollector {
    id
    nftsCount
    user {
      id
      publicAddress
      username
      webappUri
      avatar {
        id
        url
      }
      artist {
        id
      }
    }
  }
`);

const AVATAR_SIZE = 40;
export const CollectorMintsRow = ({
  collectorMintData,
}: {
  collectorMintData: FragmentType<CollectorMintsRowFragmentDoc>;
}) => {
  const { nftsCount, user } = getFragment(CollectorMintsRowFragmentDoc, collectorMintData);

  const { webappUri, publicAddress, artist, username, avatar } = user;
  return (
    <div className="flex w-full items-start justify-between py-1.5 ">
      <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
        <div className="flex items-center gap-2">
          <Avatar
            src={avatar?.url}
            walletAddress={publicAddress}
            webappUri={webappUri}
            username={username}
            size={AVATAR_SIZE}
            disableLink
            borderRadius="50%"
          />
          <div className="flex flex-col gap-0.5">
            <div className="text-sm flex items-center gap-1 font-title text-title-xxs font-medium text-black">
              <h3 className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap md:max-w-[200px]">
                {username}
              </h3>
              {artist && <VerifiedTooltip size={16} />}
            </div>
            <div className="font-base text-base-s text-base600">
              {millify(nftsCount)}
              {getSingularOrPlural({
                count: nftsCount,
                word: ' edition',
              })}
            </div>
          </div>
        </div>
      </AccessibleLink>
      <FollowButton
        userId={user.id}
        artistId={artist?.id}
        variant="primary"
        size="XS"
        sourceLocation="Collector Mints"
      />
    </div>
  );
};
